import { DEBUG } from './debug';
import * as ES from './ecmascript';
import { MakeIntrinsicClass } from './intrinsicclass';
import { EPOCHNANOSECONDS, CreateSlots, GetSlot, SetSlot } from './slots';
import { DateTimeFormat } from './intl';
import JSBI from 'jsbi';
import { BILLION, MILLION, THOUSAND } from './ecmascript';
export class Instant {
    constructor(epochNanoseconds) {
        // Note: if the argument is not passed, ToBigInt(undefined) will throw. This check exists only
        //       to improve the error message.
        if (arguments.length < 1) {
            throw new TypeError('missing argument: epochNanoseconds is required');
        }
        const ns = ES.ToBigInt(epochNanoseconds);
        ES.ValidateEpochNanoseconds(ns);
        CreateSlots(this);
        SetSlot(this, EPOCHNANOSECONDS, ns);
        if (DEBUG) {
            const repr = ES.TemporalInstantToString(this, undefined, 'auto');
            Object.defineProperty(this, '_repr_', {
                value: `${this[Symbol.toStringTag]} <${repr}>`,
                writable: false,
                enumerable: false,
                configurable: false
            });
        }
    }
    get epochSeconds() {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        const value = GetSlot(this, EPOCHNANOSECONDS);
        return JSBI.toNumber(JSBI.divide(value, BILLION));
    }
    get epochMilliseconds() {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        const value = JSBI.BigInt(GetSlot(this, EPOCHNANOSECONDS));
        return JSBI.toNumber(JSBI.divide(value, MILLION));
    }
    get epochMicroseconds() {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        const value = JSBI.BigInt(GetSlot(this, EPOCHNANOSECONDS));
        return ES.ToBigIntExternal(JSBI.divide(value, THOUSAND));
    }
    get epochNanoseconds() {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        return ES.ToBigIntExternal(JSBI.BigInt(GetSlot(this, EPOCHNANOSECONDS)));
    }
    add(temporalDurationLike) {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        return ES.AddDurationToOrSubtractDurationFromInstant('add', this, temporalDurationLike);
    }
    subtract(temporalDurationLike) {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        return ES.AddDurationToOrSubtractDurationFromInstant('subtract', this, temporalDurationLike);
    }
    until(other, options = undefined) {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        return ES.DifferenceTemporalInstant('until', this, other, options);
    }
    since(other, options = undefined) {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        return ES.DifferenceTemporalInstant('since', this, other, options);
    }
    round(optionsParam) {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        if (optionsParam === undefined)
            throw new TypeError('options parameter is required');
        const options = typeof optionsParam === 'string'
            ? ES.CreateOnePropObject('smallestUnit', optionsParam)
            : ES.GetOptionsObject(optionsParam);
        const smallestUnit = ES.GetTemporalUnit(options, 'smallestUnit', 'time', ES.REQUIRED);
        const roundingMode = ES.ToTemporalRoundingMode(options, 'halfExpand');
        const maximumIncrements = {
            hour: 24,
            minute: 1440,
            second: 86400,
            millisecond: 86400e3,
            microsecond: 86400e6,
            nanosecond: 86400e9
        };
        const roundingIncrement = ES.ToTemporalRoundingIncrement(options, maximumIncrements[smallestUnit], true);
        const ns = GetSlot(this, EPOCHNANOSECONDS);
        const roundedNs = ES.RoundInstant(ns, roundingIncrement, smallestUnit, roundingMode);
        return new Instant(roundedNs);
    }
    equals(otherParam) {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        const other = ES.ToTemporalInstant(otherParam);
        const one = GetSlot(this, EPOCHNANOSECONDS);
        const two = GetSlot(other, EPOCHNANOSECONDS);
        return JSBI.equal(JSBI.BigInt(one), JSBI.BigInt(two));
    }
    toString(optionsParam = undefined) {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        const options = ES.GetOptionsObject(optionsParam);
        let timeZone = options.timeZone;
        if (timeZone !== undefined)
            timeZone = ES.ToTemporalTimeZone(timeZone);
        // Although TS doesn't acknowledge it, below here `timeZone` is a Temporal.TimeZoneProtocol
        const { precision, unit, increment } = ES.ToSecondsStringPrecision(options);
        const roundingMode = ES.ToTemporalRoundingMode(options, 'trunc');
        const ns = GetSlot(this, EPOCHNANOSECONDS);
        const roundedNs = ES.RoundInstant(ns, increment, unit, roundingMode);
        const roundedInstant = new Instant(roundedNs);
        return ES.TemporalInstantToString(roundedInstant, timeZone, precision);
    }
    toJSON() {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        return ES.TemporalInstantToString(this, undefined, 'auto');
    }
    toLocaleString(locales = undefined, options = undefined) {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        return new DateTimeFormat(locales, options).format(this);
    }
    valueOf() {
        throw new TypeError('use compare() or equals() to compare Temporal.Instant');
    }
    toZonedDateTime(item) {
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        if (!ES.IsObject(item)) {
            throw new TypeError('invalid argument in toZonedDateTime');
        }
        const calendarLike = item.calendar;
        if (calendarLike === undefined) {
            throw new TypeError('missing calendar property in toZonedDateTime');
        }
        const calendar = ES.ToTemporalCalendar(calendarLike);
        const temporalTimeZoneLike = item.timeZone;
        if (temporalTimeZoneLike === undefined) {
            throw new TypeError('missing timeZone property in toZonedDateTime');
        }
        const timeZone = ES.ToTemporalTimeZone(temporalTimeZoneLike);
        return ES.CreateTemporalZonedDateTime(GetSlot(this, EPOCHNANOSECONDS), timeZone, calendar);
    }
    toZonedDateTimeISO(itemParam) {
        let item = itemParam;
        if (!ES.IsTemporalInstant(this))
            throw new TypeError('invalid receiver');
        if (ES.IsObject(item)) {
            const timeZoneProperty = item.timeZone;
            if (timeZoneProperty !== undefined) {
                item = timeZoneProperty;
            }
        }
        const timeZone = ES.ToTemporalTimeZone(item);
        const calendar = ES.GetISO8601Calendar();
        return ES.CreateTemporalZonedDateTime(GetSlot(this, EPOCHNANOSECONDS), timeZone, calendar);
    }
    static fromEpochSeconds(epochSecondsParam) {
        const epochSeconds = ES.ToNumber(epochSecondsParam);
        const epochNanoseconds = JSBI.multiply(JSBI.BigInt(epochSeconds), BILLION);
        ES.ValidateEpochNanoseconds(epochNanoseconds);
        return new Instant(epochNanoseconds);
    }
    static fromEpochMilliseconds(epochMillisecondsParam) {
        const epochMilliseconds = ES.ToNumber(epochMillisecondsParam);
        const epochNanoseconds = JSBI.multiply(JSBI.BigInt(epochMilliseconds), MILLION);
        ES.ValidateEpochNanoseconds(epochNanoseconds);
        return new Instant(epochNanoseconds);
    }
    static fromEpochMicroseconds(epochMicrosecondsParam) {
        const epochMicroseconds = ES.ToBigInt(epochMicrosecondsParam);
        const epochNanoseconds = JSBI.multiply(epochMicroseconds, THOUSAND);
        ES.ValidateEpochNanoseconds(epochNanoseconds);
        return new Instant(epochNanoseconds);
    }
    static fromEpochNanoseconds(epochNanosecondsParam) {
        const epochNanoseconds = ES.ToBigInt(epochNanosecondsParam);
        ES.ValidateEpochNanoseconds(epochNanoseconds);
        return new Instant(epochNanoseconds);
    }
    static from(item) {
        if (ES.IsTemporalInstant(item)) {
            return new Instant(GetSlot(item, EPOCHNANOSECONDS));
        }
        return ES.ToTemporalInstant(item);
    }
    static compare(oneParam, twoParam) {
        const one = ES.ToTemporalInstant(oneParam);
        const two = ES.ToTemporalInstant(twoParam);
        const oneNs = GetSlot(one, EPOCHNANOSECONDS);
        const twoNs = GetSlot(two, EPOCHNANOSECONDS);
        if (JSBI.lessThan(oneNs, twoNs))
            return -1;
        if (JSBI.greaterThan(oneNs, twoNs))
            return 1;
        return 0;
    }
}
Symbol.toStringTag;
MakeIntrinsicClass(Instant, 'Temporal.Instant');
//# sourceMappingURL=instant.js.map